/**
 * De blokjes waaruit een regel is opgebouwd: de ALS-boom en de DAN-lijst.
 *
 * Elk blokje is een rijtje witte tags op een half-transparant vlak. Klikken op een tag
 * opent de invoer voor dat deel — veld, operator, waarde — zodat een regel te lezen is
 * als een zin en toch aan te passen zonder formuletaal.
 *
 * Kleuren volgen de designspecificatie: voorwaarden blauw, acties donkerblauw,
 * tabelwaarden paars. Die drie zijn hier bewust hard genoteerd en niet als
 * afdelingskleur: ze horen bij de editor, niet bij een afdeling.
 */
import type {
  Actie,
  Voorwaarde,
  Waarde,
  Vergelijker,
  Rekenoperator,
} from '@houtstaat/engine';

import type { BlokkenBibliotheek } from '../api';
import { Keuzelijst } from './Keuzelijst';

export const BLOKKLEUR = {
  voorwaarde: '#0070C0',
  voorwaardeOf: '#005a9e',
  actie: '#001aaa',
  tabel: '#7030A0',
} as const;

export const VERGELIJKERS: Array<{ sleutel: Vergelijker; label: string; zonderRechts?: boolean }> = [
  { sleutel: 'gelijk', label: 'is gelijk aan' },
  { sleutel: 'ongelijk', label: 'is niet gelijk aan' },
  { sleutel: 'groter', label: 'is groter dan' },
  { sleutel: 'groter-of-gelijk', label: 'is groter dan of gelijk aan' },
  { sleutel: 'kleiner', label: 'is kleiner dan' },
  { sleutel: 'kleiner-of-gelijk', label: 'is kleiner dan of gelijk aan' },
  { sleutel: 'bevat', label: 'bevat' },
  { sleutel: 'in', label: 'staat in de lijst' },
  { sleutel: 'is-gevuld', label: 'is ingevuld', zonderRechts: true },
  { sleutel: 'is-leeg', label: 'is leeg', zonderRechts: true },
  { sleutel: 'is-waar', label: 'is waar', zonderRechts: true },
];

export const OPERATOREN: Array<{ sleutel: Rekenoperator; label: string; teken: string }> = [
  { sleutel: 'plus', label: 'optellen', teken: '+' },
  { sleutel: 'min', label: 'aftrekken', teken: '−' },
  { sleutel: 'maal', label: 'vermenigvuldigen', teken: '×' },
  { sleutel: 'gedeeld', label: 'delen', teken: '÷' },
  { sleutel: 'minimum', label: 'kleinste van', teken: 'min' },
  { sleutel: 'maximum', label: 'grootste van', teken: 'max' },
  { sleutel: 'afronden', label: 'afronden', teken: '≈' },
];

/** Een witte tag op een gekleurd blok. */
export function Tag({
  children,
  kleur,
  onClick,
  titel,
}: {
  children: React.ReactNode;
  kleur?: string;
  onClick?: () => void;
  titel?: string;
}) {
  const inhoud = (
    <span
      className="hs-mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 6px',
        borderRadius: 'var(--r-tag)',
        background: kleur ?? '#fff',
        color: kleur ? '#fff' : 'var(--tekst)',
        fontSize: 11,
        whiteSpace: 'nowrap',
        maxWidth: 420,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={titel}
    >
      {/* De inhoud staat in een eigen span: `text-overflow` werkt niet op een
          flexcontainer, waardoor een lange formule middenin een woord werd afgekapt
          zonder puntjes. De volledige tekst zit in de `title`. */}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </span>
    </span>
  );
  if (!onClick) return inhoud;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex' }}
      title={titel ?? 'Aanpassen'}
    >
      {inhoud}
    </button>
  );
}

/** Het half-transparante vlak waarop de tags van één blok staan. */
function Blok({
  children,
  onWeg,
}: {
  children: React.ReactNode;
  onWeg?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        flexWrap: 'wrap',
        alignSelf: 'flex-start',
        padding: '5px 7px',
        borderRadius: 'var(--r-knop)',
        background: 'rgba(255,255,255,.16)',
      }}
    >
      {children}
      {onWeg ? (
        <button
          type="button"
          onClick={onWeg}
          title="Dit blok verwijderen"
          style={{
            all: 'unset',
            cursor: 'pointer',
            color: 'rgba(255,255,255,.75)',
            fontSize: 12,
            padding: '0 2px',
          }}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

/** Leest een waarde als korte tekst, voor de tag. */
export function waardeTekst(waarde: Waarde, blokken: BlokkenBibliotheek): string {
  switch (waarde.herkomst) {
    case 'veld':
      return blokken.velden.find((v) => v.pad === waarde.pad)?.label ?? waarde.pad;
    case 'tabel':
      return blokken.tabelwaarden.find((t) => t.pad === waarde.pad)?.label ?? waarde.pad;
    case 'constant':
      return String(waarde.waarde);
    case 'variabele':
      return waarde.naam;
    case 'berekend':
      return waarde.termen
        .map((t) => waardeTekst(t, blokken))
        .join(` ${OPERATOREN.find((o) => o.sleutel === waarde.operator)?.teken ?? '?'} `);
    case 'keuze':
      return `als … dan ${waardeTekst(waarde.dan, blokken)} anders ${waardeTekst(waarde.anders, blokken)}`;
  }
}

/** De kleur van een waardetag, naar herkomst. */
export const waardeKleur = (waarde: Waarde): string | undefined =>
  waarde.herkomst === 'tabel' ? BLOKKLEUR.tabel : undefined;

// ── Voorwaarden ─────────────────────────────────────────────────────────────

/**
 * De ALS-kant. Alleen de bovenste laag is bewerkbaar: een EN met vergelijkingen, of
 * een OF met groepen. Diepere nesting is in het model mogelijk maar wordt hier
 * bewust niet aangeboden — dan wordt de regel voor een afschrijver onleesbaar.
 */
export function Voorwaardenlijst({
  voorwaarde,
  blokken,
  bewerkbaar,
  onWijzig,
  onKiesWaarde,
}: {
  voorwaarde: Voorwaarde;
  blokken: BlokkenBibliotheek;
  bewerkbaar: boolean;
  onWijzig: (nieuw: Voorwaarde) => void;
  onKiesWaarde: (huidig: Waarde, klaar: (nieuw: Waarde) => void) => void;
}) {
  const kinderen = voorwaarde.soort === 'en' || voorwaarde.soort === 'of' ? voorwaarde.kinderen : [voorwaarde];
  const isOf = voorwaarde.soort === 'of';

  const zetKind = (index: number, nieuw: Voorwaarde | null) => {
    const lijst = kinderen.slice();
    if (nieuw) lijst[index] = nieuw;
    else lijst.splice(index, 1);
    onWijzig({ soort: isOf ? 'of' : 'en', kinderen: lijst });
  };

  if (kinderen.length === 0) {
    return (
      <p style={{ color: 'rgba(255,255,255,.85)', fontSize: 11.5, margin: 0 }}>
        Geen voorwaarden: deze bewerking draait altijd.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {kinderen.map((kind, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {i > 0 ? (
            <Tag kleur={isOf ? BLOKKLEUR.voorwaardeOf : undefined} titel={isOf ? 'of' : 'en'}>
              {isOf ? 'OF' : 'EN'}
            </Tag>
          ) : null}
          <Voorwaardeblok
            voorwaarde={kind}
            blokken={blokken}
            bewerkbaar={bewerkbaar}
            onWijzig={(nieuw) => zetKind(i, nieuw)}
            onWeg={bewerkbaar ? () => zetKind(i, null) : undefined}
            onKiesWaarde={onKiesWaarde}
          />
        </div>
      ))}
    </div>
  );
}

function Voorwaardeblok({
  voorwaarde,
  blokken,
  bewerkbaar,
  onWijzig,
  onWeg,
  onKiesWaarde,
}: {
  voorwaarde: Voorwaarde;
  blokken: BlokkenBibliotheek;
  bewerkbaar: boolean;
  onWijzig: (nieuw: Voorwaarde) => void;
  onWeg?: () => void;
  onKiesWaarde: (huidig: Waarde, klaar: (nieuw: Waarde) => void) => void;
}) {
  if (voorwaarde.soort === 'niet') {
    return (
      <Blok onWeg={onWeg}>
        <Tag>NIET</Tag>
        <Voorwaardeblok
          voorwaarde={voorwaarde.kind}
          blokken={blokken}
          bewerkbaar={bewerkbaar}
          onWijzig={(nieuw) => onWijzig({ soort: 'niet', kind: nieuw })}
          onKiesWaarde={onKiesWaarde}
        />
      </Blok>
    );
  }

  if (voorwaarde.soort === 'en' || voorwaarde.soort === 'of') {
    return (
      <Blok onWeg={onWeg}>
        <Tag kleur={BLOKKLEUR.voorwaardeOf}>{voorwaarde.soort === 'of' ? 'OF-groep' : 'EN-groep'}</Tag>
        <span style={{ color: 'rgba(255,255,255,.85)', fontSize: 11 }}>
          {voorwaarde.kinderen.length} voorwaarden
        </span>
      </Blok>
    );
  }

  const vergelijker = VERGELIJKERS.find((v) => v.sleutel === voorwaarde.vergelijker);
  return (
    <Blok onWeg={onWeg}>
      <Tag
        kleur={waardeKleur(voorwaarde.links)}
        onClick={
          bewerkbaar
            ? () => onKiesWaarde(voorwaarde.links, (nieuw) => onWijzig({ ...voorwaarde, links: nieuw }))
            : undefined
        }
      >
        {waardeTekst(voorwaarde.links, blokken)}
      </Tag>
      {bewerkbaar ? (
        <Keuzelijst
          className="hs-mono"
          value={voorwaarde.vergelijker}
          onChange={(e) => onWijzig({ ...voorwaarde, vergelijker: e.target.value as Vergelijker })}
          style={{
            border: 0,
            borderRadius: 'var(--r-tag)',
            padding: '2px 4px',
            fontSize: 11,
            background: '#fff',
          }}
        >
          {VERGELIJKERS.map((v) => (
            <option key={v.sleutel} value={v.sleutel}>
              {v.label}
            </option>
          ))}
        </Keuzelijst>
      ) : (
        <Tag>{vergelijker?.label ?? voorwaarde.vergelijker}</Tag>
      )}
      {!vergelijker?.zonderRechts ? (
        <Tag
          kleur={voorwaarde.rechts ? waardeKleur(voorwaarde.rechts) : undefined}
          onClick={
            bewerkbaar
              ? () =>
                  onKiesWaarde(voorwaarde.rechts ?? { herkomst: 'constant', waarde: '' }, (nieuw) =>
                    onWijzig({ ...voorwaarde, rechts: nieuw }),
                  )
              : undefined
          }
        >
          {voorwaarde.rechts ? waardeTekst(voorwaarde.rechts, blokken) : '…'}
        </Tag>
      ) : null}
    </Blok>
  );
}

// ── Acties ──────────────────────────────────────────────────────────────────

const ACTIELABEL: Record<Actie['soort'], string> = {
  'activeer-bewerking': 'Activeer bewerking',
  'parameter-instellen': 'Parameter instellen',
  'waarde-berekenen': 'Waarde berekenen',
  'label-tonen': 'Label in het blok',
  'toelichting-tonen': 'Toelichting',
};

export function Actielijst({
  acties,
  blokken,
  bewerkbaar,
  onWijzig,
  onKiesWaarde,
}: {
  acties: Actie[];
  blokken: BlokkenBibliotheek;
  bewerkbaar: boolean;
  onWijzig: (nieuw: Actie[]) => void;
  onKiesWaarde: (huidig: Waarde, klaar: (nieuw: Waarde) => void) => void;
}) {
  const zet = (index: number, nieuw: Actie | null) => {
    const lijst = acties.slice();
    if (nieuw) lijst[index] = nieuw;
    else lijst.splice(index, 1);
    onWijzig(lijst);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {acties.map((actie, i) => (
        <Blok key={i} onWeg={bewerkbaar ? () => zet(i, null) : undefined}>
          <Tag titel={ACTIELABEL[actie.soort]}>{ACTIELABEL[actie.soort]}</Tag>

          {actie.soort === 'parameter-instellen' || actie.soort === 'waarde-berekenen' ? (
            <>
              {bewerkbaar ? (
                <input
                  className="hs-mono"
                  value={actie.parameter}
                  onChange={(e) => zet(i, { ...actie, parameter: e.target.value })}
                  style={{
                    border: 0,
                    borderRadius: 'var(--r-tag)',
                    padding: '2px 5px',
                    fontSize: 11,
                    width: 130,
                  }}
                  placeholder="parameternaam"
                />
              ) : (
                <Tag>{actie.parameter}</Tag>
              )}
              <span style={{ color: '#fff', fontSize: 11 }}>=</span>
              <Tag
                kleur={waardeKleur(actie.waarde)}
                onClick={bewerkbaar ? () => onKiesWaarde(actie.waarde, (nieuw) => zet(i, { ...actie, waarde: nieuw })) : undefined}
                titel={waardeTekst(actie.waarde, blokken)}
              >
                {waardeTekst(actie.waarde, blokken)}
              </Tag>
            </>
          ) : null}

          {actie.soort === 'label-tonen' || actie.soort === 'toelichting-tonen' ? (
            bewerkbaar ? (
              <input
                className="hs-mono"
                value={actie.patroon}
                onChange={(e) => zet(i, { ...actie, patroon: e.target.value })}
                style={{
                  border: 0,
                  borderRadius: 'var(--r-tag)',
                  padding: '2px 5px',
                  fontSize: 11,
                  width: 280,
                }}
                placeholder="{aantal} × {zaagLengte} × {zaagBreedte}"
              />
            ) : (
              <Tag titel={actie.patroon}>{actie.patroon}</Tag>
            )
          ) : null}
        </Blok>
      ))}
    </div>
  );
}
